// deps
import React, { ReactElement, useContext, useMemo, ReactNode, useCallback, memo, useState } from "react";
import invariant from "tiny-invariant";

// internal deps
import { LinkAccountView } from "@swiggy-private/payment-ui";
import { CheckoutActionTypes, CheckoutContext, closeWebview, getBridgeSdk } from "@swiggy-private/checkout-core";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaylaterLinkedContext } from "@payment-client/contexts/PaylaterLinkedContext";
import { WalletBalanceContext } from "@payment-client/contexts/WalletBalanceContext";

// constants
import { OOPS_SOMETHING_WENT_WRONG, VERIFY_AND_PAY } from "@payment-client/constants/TextDict";
import { LAZY_PAY_META } from "@payment-client/constants/Options";
import { PAYLATER_TYPES, WALLET_CODES } from "@swiggy-private/payment-types";
import { PAYLATER_DISPLAY_NAMES } from "@swiggy-private/payment-core";

// helpers
import { findPayLaterPaymentMethod, getLinkPayLaterPageTitle } from "@payment-client/helpers/PaylaterHelper";
import { getPaymentMethodIconUrl } from "@payment-client/helpers/PaymentHelper";
import { isEmpty } from "@swiggy-private/js-utils";
import { showNativeAlert } from "@payment-client/utils/Native";

// components
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import PaymentModesWrapper from "@payment-client/components/payment-modes/components/payment-modes-wrapper/PaymentModesWrapper";

// hooks
import { getCloudinaryUrl, useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";
import { useOnBack } from "@swiggy-private/checkout-core";

// apis
import { RouteProps } from "@payment-client/interfaces/Router";
import { usePayLaterVerifyOtp } from "@payment-client/hooks/UsePayLaterVerifyOtp";
import {
    clickResendOtpEvent,
    clickVerifyProceedEvent,
    impressionResendOtpEvent,
    impressionVerifyProceedEvent,
} from "@payment-client/analytics/events/LinkPayLater";
import { CONTEXT } from "@payment-client/analytics/constants";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { LINK_ACCOUNT_SOURCE } from "@swiggy-private/native-bridge";

interface PaymentLinkPayLaterProps
    extends RouteProps<{ payLaterCode: string; placeOrderOnSuccess: boolean; mobileNumber: string }> {
    isPaymentModeRoute?: boolean;
}

const PaymentLinkPayLaterWrapper = function PaymentLinkPayLaterWrapper(props: {
    children: ReactNode;
    linkPayLaterCode: PAYLATER_TYPES;
    isPaymentModeRoute: boolean;
}): ReactElement {
    const { children, isPaymentModeRoute } = props;
    if (isPaymentModeRoute) {
        return (
            <PaymentModesWrapper title={getLinkPayLaterPageTitle(props.linkPayLaterCode)}>
                {children}
            </PaymentModesWrapper>
        );
    }

    const header = <CommonHeaderContent title={getLinkPayLaterPageTitle(props.linkPayLaterCode)} />;
    return <CommonUIWrapper header={header} content={props.children} />;
};

export const PaymentLinkPayLater = memo(function PaymentLinkPayLater({
    data,
    isPaymentModeRoute = false,
}: PaymentLinkPayLaterProps): ReactElement | null {
    const isMountedRef = useMountedRef();
    const [showSuccessBS, setShowSuccessBS] = useState<boolean>(false);
    const payLaterCode = data ? data.payLaterCode : "";
    const mobileNumber = data ? data.mobileNumber : "";
    const placeOrderOnSuccess = data?.placeOrderOnSuccess || false;
    const { paymentResponse } = useContext(ShowPaymentContext);
    const onBack = useOnBack();
    const { refetchPaylaterLinkStatus } = useContext(PaylaterLinkedContext);
    const { refetch } = useContext(WalletBalanceContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { verifyOtp, resendOtp, ...actions } = usePayLaterVerifyOtp(payLaterCode, mobileNumber);
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);

    const _resendOtp = useCallback(async () => {
        clickResendOtpEvent(payLaterCode, CONTEXT.LINK_WALLET_SOURCE_PAYMENT, clickEvent);
        try {
            await resendOtp();
        } catch (err) {
            // noop
        }
    }, [clickEvent, payLaterCode, resendOtp]);

    const _verifyOtp = useCallback(
        async (otpCode: string) => {
            if (actions.verifyInProgress) {
                return;
            }
            clickVerifyProceedEvent(payLaterCode, CONTEXT.LINK_WALLET_SOURCE_PAYMENT, clickEvent);

            try {
                const response = await verifyOtp(otpCode);
                if (isMountedRef.current) {
                    if (response.isOk()) {
                        if (payLaterCode === PAYLATER_TYPES.PAYTM_POSTPAID) {
                            refetch(WALLET_CODES.paytm);
                            getBridgeSdk()
                                .sendLinkAccountStatusToNative({
                                    paymentCode: WALLET_CODES.paytm,
                                    status: "SUCCESS",
                                    source: LINK_ACCOUNT_SOURCE.PAYMENTS,
                                    linkedMobileNumber: mobileNumber,
                                })
                                .catch(err => {
                                    __DEV__ && console.error(err);
                                });
                        }

                        if (paymentResponse && !isPaymentModeRoute) {
                            refetchPaylaterLinkStatus(payLaterCode);
                        }
                        setShowSuccessBS(true);
                    } else {
                        showNativeAlert({ message: response.unwrapErr() });
                    }
                }
            } catch (err) {
                isMountedRef.current && showNativeAlert({ message: OOPS_SOMETHING_WENT_WRONG });
            }
        },
        [
            actions.verifyInProgress,
            payLaterCode,
            clickEvent,
            verifyOtp,
            isMountedRef,
            paymentResponse,
            isPaymentModeRoute,
            refetch,
            refetchPaylaterLinkStatus,
        ],
    );

    const otpActions = useMemo(() => ({ verifyOtp: _verifyOtp, resendOtp: _resendOtp, ...actions }), [
        actions,
        _verifyOtp,
        _resendOtp,
    ]);

    if (payLaterCode.length === 0 || isEmpty(mobileNumber)) {
        invariant(false, "Missing route data");
    }

    useEffectOnce(() => {
        impressionVerifyProceedEvent(payLaterCode, CONTEXT.LINK_WALLET_SOURCE_PAYMENT, impressionEvent);
        impressionResendOtpEvent(payLaterCode, CONTEXT.LINK_WALLET_SOURCE_PAYMENT, impressionEvent);
    });

    const { iconUrl, linkPayLaterCode, displayName } = useMemo(() => {
        if (paymentResponse && !isPaymentModeRoute) {
            const paymentMethod = findPayLaterPaymentMethod(paymentResponse.paymentGroup, payLaterCode);
            invariant(paymentMethod.isSome(), "Payment method not found: " + payLaterCode);
            const method = paymentMethod.unwrap();
            if (isEmpty(method)) {
                return {
                    iconUrl: "",
                    linkPayLaterCode: "",
                    displayName: "",
                };
            }
            return {
                iconUrl: getPaymentMethodIconUrl(method),
                linkPayLaterCode: method.name.toUpperCase(),
                displayName: PAYLATER_DISPLAY_NAMES[payLaterCode as PAYLATER_TYPES],
            };
        }
        // For Static LP linking page, where the LP method data is not present.
        return {
            iconUrl: getCloudinaryUrl(LAZY_PAY_META.iconUrl),
            linkPayLaterCode: LAZY_PAY_META.paymentCode,
            displayName: LAZY_PAY_META.displayName,
        };
    }, [isPaymentModeRoute, payLaterCode, paymentResponse]);

    const onCloseBs = useCallback(() => {
        /*
         * Success BS is currently being used only for linking flow through payment modes/deeplink,
         * in which case to avoid flicker user has to be redirected to Home page after successful linking
         */
        setShowSuccessBS(false);
        if (placeOrderOnSuccess) {
            setCheckoutState({ type: CheckoutActionTypes.POST_PAYLATER_LINKING });
            return;
        }
        if (paymentResponse && !isPaymentModeRoute) {
            /* Separate handling for lazypay vs postpaid 
            as lazypay is halfcard while postpaid is full screen page */
            onBack(payLaterCode === PAYLATER_TYPES.PAYTM_POSTPAID ? 2 : 1);
            return;
        }
        closeWebview();
    }, [isPaymentModeRoute, onBack, payLaterCode, paymentResponse, placeOrderOnSuccess, setCheckoutState]);

    return (
        <PaymentLinkPayLaterWrapper
            isPaymentModeRoute={isPaymentModeRoute}
            linkPayLaterCode={payLaterCode as PAYLATER_TYPES}
        >
            <LinkAccountView
                actions={otpActions}
                mobile={mobileNumber}
                iconUrl={iconUrl}
                walletCode={linkPayLaterCode}
                displayName={displayName}
                showSuccessBS={showSuccessBS}
                onCloseBS={onCloseBs}
                verifyCtaText={placeOrderOnSuccess ? VERIFY_AND_PAY : undefined}
            />
        </PaymentLinkPayLaterWrapper>
    );
});
